// ============================================================================
// Feuille du noyau : VÉRIFIER CHAQUE AFFIRMATION (moteur v36, refonte lot 6.8 —
// docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// Un QCM « coche les affirmations vraies » : chaque choix est une affirmation qui se
// tranche par un calcul (« 11 × 48 = 418 », « 5 ÷ 0,4 > 10 », « x = 3 est solution de
// 2x + 1 = 7 »). Le modèle donne, pour chaque choix, la ou les relations qui le décident
// (`kernel.claims`, dans l'ordre des choix), avec la valeur des lettres s'il y en a
// (`at` : { x: '3' }).
//
// En tête de feuille : les affirmations, chacune avec sa marque (« ? », « vraie »,
// « fausse »). L'élève calcule : chaque ligne est une chaîne d'égalités entre nombres,
// VRAIE (calcul exact) et JUSTIFIÉE maillon par maillon :
//
//   Calculer (un calcul par endroit)                         1   kernel.compute@1
//   Développer                                               1   kernel.expand@1
//   Remplacer la lettre par sa valeur                        2   kernel.substitute@1
//   Règle des signes (« 1 − (−3) » → « 1 + 3 »)              1   claims.signs@1
//   Puissance négative, produit / quotient / puissance
//     de puissances                                          1   claims.power@1
//   Simplifier une fraction, facteur commun, même
//     dénominateur, additionner les numérateurs,
//     multiplier les fractions, multiplier par l'inverse     1   claims.fraction@1
//   Angles associés (cos(π − x) = −cos(x)…), lire une
//     valeur sur le cercle (sin(5π ÷ 6) = 1 ÷ 2) — v37        1   claims.trig@1
//   Écrire comme une racine (3 = √9), simplifier la racine
//     (√40 = 2√10) — v37                                     1   claims.root@1
//   Logarithme d'un produit, d'un quotient, d'une puissance,
//     d'une racine, d'un inverse ; ln(1) = 0, ln(e) = 1 ;
//     écrire comme une puissance (ln(8) = ln(2³)) — v45       1   claims.ln@1
//   Trancher (la ligne décide au moins une affirmation)     +1   claims.decide@1
//
// Une affirmation est TRANCHÉE dès que la valeur de chacun de ses membres est écrite
// (une valeur : un nombre, une fraction irréductible, 2√3, π ÷ 3…), ou quand ses deux
// membres sont reliés par une même chaîne d'égalités : la feuille la compare et la
// marque vraie ou fausse. Le but est atteint quand toutes le sont. Une ligne juste qui
// saute des étapes reste sur la feuille, sans HORA (D7), et tranche comme les autres.
// ============================================================================

import { checkSyntax, lex, normalizeChips, type Chip } from './lexer';
import type { ScratchModel } from './model';
import { parseText, type Line } from './tree';
import * as Q from './kernel/exact';
import { normalize as normalForm, rfRat } from './kernel/normal';
import { parseStatement, substitute, symbolsOf, termKey, type Rel, type Term } from './kernel/term';
import {
  claimsFormGesture,
  claimsLnGesture,
  euclidParts,
  hasLn,
  isPrimeInt,
  reducibleLn,
  sameFormWriting,
  claimsGesture,
  factoredFraction,
  hasClaimForm,
  irreducibleTruth,
  primeFractionStep,
  sameFractionValues,
  type ClaimForm,
} from './kernel-claims-gestures';
import {
  constCmpHolds,
  constOps,
  constSameValue,
  constShapeKey,
  constStep,
  formulaText,
  kforLineAtoms,
  kforReadAtoms,
} from './kernel-formula';
import {
  hasLetters,
  isLetterValue,
  letterCounterexample,
  letterGesture,
  letterSame,
} from './kernel-claims-letters';

/* ------------------------------ pas ------------------------------ */

export const CLAIMS_STEP_IDS = {
  compute: 'kernel.compute@1',
  expand: 'kernel.expand@1',
  replace: 'kernel.substitute@1',
  signs: 'claims.signs@1',
  power: 'claims.power@1',
  fraction: 'claims.fraction@1',
  trig: 'claims.trig@1',
  root: 'claims.root@1',
  factor: 'claims.factor@1',
  scientific: 'claims.scientific@1',
  euclid: 'claims.euclid@1',
  ln: 'claims.ln@1',
  /** moteur v53 : décomposer le numérateur et le dénominateur en facteurs premiers */
  fractionPrimes: 'claims.fraction_primes@1',
  decide: 'claims.decide@1',
} as const;

export interface KClaimsRule {
  id: string;
  card: string;
  label: string;
  hora: number;
  why: string;
}

const CARD = 'Calcul';

const STEP = {
  compute: {
    id: CLAIMS_STEP_IDS.compute,
    label: 'Calculer',
    hora: 1,
    why: 'On fait un calcul (un seul par endroit) : la valeur ne change pas, elle s’écrit plus simplement.',
  },
  expand: {
    id: CLAIMS_STEP_IDS.expand,
    label: 'Développer',
    hora: 1,
    why: 'On distribue : k × (a + b) = k × a + k × b.',
  },
  // moteur v50 : affirmations en lettres
  expandLetters: {
    id: CLAIMS_STEP_IDS.expand,
    label: 'Développer',
    hora: 1,
    why: 'On développe un produit (k(a + b) = ka + kb, (a + b)(c + d) = ac + ad + bc + bd, (a − b)² = a² − 2ab + b²…) : l’expression ne change pas, elle s’écrit autrement.',
  },
  parens: {
    id: CLAIMS_STEP_IDS.expand,
    label: 'Enlever les parenthèses',
    hora: 1,
    why: 'Des parenthèses précédées de « + » s’enlèvent sans rien changer ; précédées de « − », on change le signe de chaque terme.',
  },
  reduce: {
    id: CLAIMS_STEP_IDS.compute,
    label: 'Réduire',
    hora: 1,
    why: 'On calcule les nombres et on regroupe les termes semblables (5x + 5x = 10x, x × x = x²) : l’expression ne change pas.',
  },
  factorLetters: {
    id: CLAIMS_STEP_IDS.factor,
    label: 'Factoriser',
    hora: 1,
    why: 'On met un facteur commun en évidence (ka + kb = k(a + b)) ou on reconnaît une identité remarquable : l’expression ne change pas.',
  },
  powerRules: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Règles des puissances',
    hora: 1,
    why: 'aⁿ × aᵐ = aⁿ⁺ᵐ, aⁿ ÷ aᵐ = aⁿ⁻ᵐ, (aⁿ)ᵐ = aⁿˣᵐ.',
  },
  replace: {
    id: CLAIMS_STEP_IDS.replace,
    label: 'Remplacer',
    hora: 2,
    why: 'On remplace la lettre par la valeur proposée.',
  },
  signs: {
    id: CLAIMS_STEP_IDS.signs,
    label: 'Règle des signes',
    hora: 1,
    why: 'On enlève les parenthèses d’un nombre relatif : soustraire −3, c’est ajouter 3 ; ajouter −3, c’est soustraire 3.',
  },
  powerNeg: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Puissance négative',
    hora: 1,
    why: 'a⁻ⁿ = 1 ÷ aⁿ.',
  },
  powerProduct: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Produit de puissances',
    hora: 1,
    why: 'aᵐ × aⁿ = aᵐ⁺ⁿ : on ajoute les exposants.',
  },
  powerQuotient: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Quotient de puissances',
    hora: 1,
    why: 'aᵐ ÷ aⁿ = aᵐ⁻ⁿ : on soustrait les exposants.',
  },
  powerPower: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Puissance d’une puissance',
    hora: 1,
    why: '(aᵐ)ⁿ = aᵐˣⁿ : on multiplie les exposants.',
  },
  powerExpand: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Écrire la puissance en produit',
    hora: 1,
    why: 'aⁿ = a × a × … × a : n facteurs égaux à a.',
  },
  fracSimplify: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Simplifier la fraction',
    hora: 1,
    why: 'On divise le numérateur et le dénominateur par un même nombre.',
  },
  fracFactor: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Facteur commun',
    hora: 1,
    why: 'On fait apparaître un même facteur au numérateur et au dénominateur.',
  },
  fracCommon: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Même dénominateur',
    hora: 1,
    why: 'On écrit les fractions avec le même dénominateur.',
  },
  fracAdd: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Additionner les numérateurs',
    hora: 1,
    why: 'Même dénominateur : on additionne les numérateurs, on garde le dénominateur.',
  },
  fracMul: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Multiplier les fractions',
    hora: 1,
    why: 'On multiplie les numérateurs entre eux, et les dénominateurs entre eux.',
  },
  fracInverse: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Multiplier par l’inverse',
    hora: 1,
    why: 'Diviser par une fraction, c’est multiplier par son inverse.',
  },
  trig: {
    id: CLAIMS_STEP_IDS.trig,
    label: 'Angles associés',
    hora: 1,
    why: 'cos(π − x) = −cos(x), sin(π − x) = sin(x), cos(−x) = cos(x), sin(−x) = −sin(x), cos(π + x) = −cos(x), cos(π ÷ 2 − x) = sin(x)… : on se ramène à un angle entre 0 et π ÷ 2.',
  },
  circle: {
    id: CLAIMS_STEP_IDS.trig,
    label: 'Lire sur le cercle',
    hora: 1,
    why: 'Sur le cercle trigonométrique, cet angle est associé à un angle entre 0 et π ÷ 2 : on y lit sa valeur, avec son signe.',
  },
  rootWrite: {
    id: CLAIMS_STEP_IDS.root,
    label: 'Écrire comme une racine',
    hora: 1,
    why: 'Un nombre positif a est la racine carrée de a² : 3 = √9. On compare ensuite les nombres sous la racine.',
  },
  rootSimplify: {
    id: CLAIMS_STEP_IDS.root,
    label: 'Simplifier la racine',
    hora: 1,
    why: '√(a² × b) = a√b : on sort les carrés de la racine (√40 = √(4 × 10) = 2√10).',
  },
  decompose: {
    id: CLAIMS_STEP_IDS.factor,
    label: 'Décomposer',
    hora: 1,
    why: 'On écrit un nombre comme le produit de deux entiers plus grands que 1 (84 = 2 × 42, puis 42 = 2 × 21…).',
  },
  // moteur v53 (lot 6.57) : une fraction irréductible par les facteurs premiers
  fracPrimes: {
    id: CLAIMS_STEP_IDS.fractionPrimes,
    label: 'Décomposer en facteurs premiers',
    hora: 2,
    why: 'On décompose le numérateur et le dénominateur en facteurs premiers : s’ils n’ont aucun facteur commun, la fraction est irréductible (16 ÷ 51 = 2⁴ ÷ (3 × 17)).',
  },
  powerGroup: {
    id: CLAIMS_STEP_IDS.power,
    label: 'Regrouper en puissance',
    hora: 1,
    why: 'Un même facteur répété s’écrit avec une puissance : 2 × 2 × 2 = 2³.',
  },
  cancel: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Barrer les facteurs communs',
    hora: 1,
    why: 'On barre un même facteur en haut et en bas de la fraction : (2 × 2 × 3) ÷ (2 × 3 × 5) = 2 ÷ 5.',
  },
  decimalFrac: {
    id: CLAIMS_STEP_IDS.fraction,
    label: 'Fraction décimale',
    hora: 1,
    why: 'Un nombre décimal s’écrit comme une fraction de dénominateur 10, 100, 1000… : 0,125 = 125 ÷ 1000.',
  },
  euclid: {
    id: CLAIMS_STEP_IDS.euclid,
    label: 'Division euclidienne',
    hora: 1,
    why: 'On écrit la division euclidienne : nombre = diviseur × quotient + reste, le reste entre 1 et le diviseur moins 1 (17 = 5 × 3 + 2).',
  },
  sciWrite: {
    id: CLAIMS_STEP_IDS.scientific,
    label: 'Puissance de 10',
    hora: 1,
    why: 'On déplace la virgule et l’exposant de 10 compte les rangs : 45 000 = 4,5 × 10⁴, 0,5 × 10⁻³ = 5 × 10⁻⁴.',
  },
  // moteur v45 (lot 6.43) : les règles de ln
  lnProduct: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Logarithme d’un produit',
    hora: 1,
    why: 'ln(a × b) = ln(a) + ln(b), pour a > 0 et b > 0 (de même avec log).',
  },
  lnQuotient: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Logarithme d’un quotient',
    hora: 1,
    why: 'ln(a ÷ b) = ln(a) − ln(b), pour a > 0 et b > 0 (de même avec log).',
  },
  lnPower: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Logarithme d’une puissance',
    hora: 1,
    why: 'ln(aⁿ) = n × ln(a), pour a > 0 (de même avec log).',
  },
  lnRoot: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Logarithme d’une racine',
    hora: 1,
    why: 'ln(√a) = (1 ÷ 2) × ln(a), pour a > 0 (de même avec log).',
  },
  lnInverse: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Logarithme d’un inverse',
    hora: 1,
    why: 'ln(1 ÷ a) = −ln(a), pour a > 0 (de même avec log).',
  },
  lnValue: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Valeur à connaître',
    hora: 1,
    why: 'ln(1) = 0, ln(e) = 1, ln(eˣ) = x ; log(1) = 0, log(10) = 1, log(10ˣ) = x.',
  },
  lnWrite: {
    id: CLAIMS_STEP_IDS.ln,
    label: 'Écrire comme une puissance',
    hora: 1,
    why: 'On écrit le nombre comme une puissance (8 = 2³, 0,01 = 10⁻², √e = e^(1 ÷ 2)) pour se servir de ln(aⁿ) = n × ln(a).',
  },
} as const;
type StepKind = keyof typeof STEP;

const DECIDE: KClaimsRule = {
  id: CLAIMS_STEP_IDS.decide,
  card: CARD,
  label: 'Trancher',
  hora: 1,
  why: '',
};

/* ------------------------------ modèle ------------------------------ */

export interface KClaimsVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step'
    | 'dup';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  /** le noyau ne sait pas vérifier ce passage */
  unknown?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: KClaimsRule;
  /** affirmations que la ligne tranche (indices, avec leur vérité) */
  decided?: { index: number; truth: boolean }[];
  cmove?: CMove;
}

/** ce que la ligne apprend à la feuille */
interface CMove {
  text: string;
  /** membres de la chaîne (clés de forme), reliés par « = » */
  keys: string[];
  terms: Term[];
  /** affirmations remplacées (lettre → valeur) par cette ligne */
  replaced: number[];
}

interface ClaimRel {
  rel: Rel;
  /** membres, lettres remplacées par leur valeur */
  l: Term;
  r: Term;
  /** membres tels qu'écrits (avec les lettres) */
  rawL: Term;
  rawR: Term;
  text: string;
}

export interface KClaim {
  index: number;
  rels: ClaimRel[];
  /** valeurs des lettres (« x = 3 ») */
  at: Record<string, Term> | null;
  atText: string | null;
  /** vérité exacte (calculée au départ) */
  truth: boolean;
  /** forme exigée du membre de droite (moteur v39) */
  form: ClaimForm | null;
  /** le membre de droite, tel qu'écrit, a-t-il cette forme ? */
  formOk: boolean;
  /**
   * moteur v40 : affirmation de PROPRIÉTÉ d'une écriture seule (« 29 » nombre premier,
   * « 6 ÷ 9 » fraction irréductible, « 4182 » divisible par 6) ; `rels` = l'écriture
   * reliée à elle-même (pour l'écrire, la remplacer, la reconnaître sur la feuille)
   */
  prop: ClaimProp | null;
  /** la propriété est niée (« 51 n'est pas premier ») */
  negated: boolean;
  /**
   * moteur v50 : une IDENTITÉ en lettres (« (x − 3)² = x² − 6x + 9 »), sans valeur
   * proposée : vraie si les membres sont égaux pour toutes les valeurs des lettres
   */
  identity?: boolean;
}

export type ClaimProp = { kind: 'form'; form: ClaimForm } | { kind: 'divisible'; d: bigint };

export interface KClaimsState {
  model: ScratchModel;
  claims: KClaim[];
  /** union des membres reliés par « = » : clé de forme → racine */
  parent: Map<string, string>;
  /** une valeur écrite dans la classe (racine → terme) */
  value: Map<string, Term>;
  /** chaque membre écrit (clé → terme) : la forme d'une écriture (moteur v39) */
  terms: Map<string, Term>;
  /** expressions présentes sur la feuille (sources d'un calcul) */
  known: Set<string>;
  /** membres avec lettres d'une affirmation → son indice (la dernière qui l'écrit) */
  rawSides: Map<string, number>;
  /** membres avec lettres → toutes les affirmations qui l'écrivent (moteur v39 : une même
   *  expression testée pour plusieurs valeurs, « 4x − 7 ≥ 5 » pour x = 3, 2, 0…) */
  rawSidesAll: Map<string, number[]>;
  /** moteur v40 : QCM à réponse unique, on tranche aussi par élimination */
  single: boolean;
  /** moteur v45 : la lettre e est la constante e (aucune affirmation ne lui donne de valeur) */
  eConst: boolean;
  /** moteur v50 : des affirmations en lettres (identités) — on calcule avec les lettres */
  letters: boolean;
  replaced: Set<number>;
  written: Set<string>;
  path: string[];
  traps: { line: Line; title: string; why: string; keys: [string, string] }[];
  lines: number;
}

export class ClaimsModelError extends Error {}

/* ------------------------------ lecture ------------------------------ */

export function kclaimsLineAtoms(text: string): string[] {
  return kforLineAtoms(text);
}

function exprOf(text: string, e = true): Term | null {
  const p = parseStatement(text);
  return p.ok && p.s.k === 'expr' ? (e ? withE(p.s.e) : p.s.e) : null;
}

const E_TERM: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: Q.ONE } };
/**
 * moteur v45 : la constante e s'écrit « e » (« ln(e) », « √e », « e³ ») ; la feuille la lit
 * exp(1) — sauf si une affirmation donne une valeur à une lettre e.
 */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E_TERM : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) };
    default:
      return t;
  }
}

/** lettres d'un terme (π n'en est pas une) */
function lettersOf(t: Term): string[] {
  return [...symbolsOf(t)].filter((n) => n !== 'π');
}
const isConst = (t: Term): boolean => lettersOf(t).length === 0;

const key = (t: Term): string => constShapeKey(t);

/* ------------------------------ valeurs ------------------------------ */

const isNumT = (t: Term): t is { k: 'num'; v: Q.Rat } => t.k === 'num';

function isNegation(t: Term): t is { k: 'mul'; c: Term[] } {
  return (
    t.k === 'mul' &&
    t.c.length === 2 &&
    isNumT(t.c[0]!) &&
    Q.eq(t.c[0]!.v, Q.rat(-1)) &&
    !isNumT(t.c[1]!)
  );
}

/** entier écrit (« 18 », « −3 ») */
function intOf(t: Term): bigint | null {
  if (isNumT(t) && Q.isInteger(t.v)) return t.v.n;
  if (isNegation(t)) {
    const x = intOf(t.c[1]!);
    return x === null ? null : -x;
  }
  return null;
}

/**
 * la valeur ENTIÈRE exacte d'une écriture sans lettre (« 40² + 40 + 41 » → 1681), null si
 * elle n'est pas entière ou pas calculable exactement (moteur v40)
 */
function intValueOf(t: Term): bigint | null {
  const lit = intOf(t);
  if (lit !== null) return lit;
  const n = normalForm(t);
  if (!n.ok) return null;
  const r = rfRat(n.rf);
  return r && Q.isInteger(r) ? r.n : null;
}

const gcdB = (a: bigint, b: bigint): bigint => {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y) [x, y] = [y, x % y];
  return x;
};

/** fraction d'entiers écrite (« 18 ÷ 45 »), ou entier (dénominateur 1) */
function fracOf(t: Term): { p: bigint; q: bigint } | null {
  if (t.k === 'div') {
    const p = intOf(t.a);
    const q = intOf(t.b);
    if (p === null || q === null || q === 0n) return null;
    return { p, q };
  }
  if (isNegation(t)) {
    const f = fracOf(t.c[1]!);
    return f && t.c[1]!.k === 'div' ? { p: -f.p, q: f.q } : null;
  }
  return null;
}

/** nombre rationnel écrit comme une valeur : nombre, fraction irréductible */
function ratValue(t: Term): Q.Rat | null {
  if (isNumT(t)) return t.v;
  const f = fracOf(t);
  if (f && f.q > 1n && gcdB(f.p, f.q) === 1n) return Q.div(Q.rat(f.p), Q.rat(f.q));
  if (isNegation(t)) {
    const r = ratValue(t.c[1]!);
    return r ? Q.neg(r) : null;
  }
  return null;
}

/** une racine √n avec un facteur carré (√48 = 4√3) : pas encore simplifiée */
function reducibleRoot(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      if (t.f === 'sqrt' && isNumT(t.a) && Q.isInteger(t.a.v) && t.a.v.n > 1n) {
        const n = t.a.v.n;
        for (let d = 2n; d * d <= n; d++) if (n % (d * d) === 0n) return true;
      }
      return reducibleRoot(t.a);
    case 'add':
    case 'mul':
      return t.c.some(reducibleRoot);
    case 'div':
    case 'pow':
    case 'binom':
      return reducibleRoot(t.a) || reducibleRoot(t.b);
    default:
      return false;
  }
}

/**
 * Une VALEUR écrite, sans calcul en attente : un nombre, une fraction irréductible, une
 * valeur irrationnelle simplifiée (2√3, π ÷ 3, ln(2)).
 */
/** « 3 ÷ 0 » : une division d'un nombre par le nombre 0 — elle n'a pas de valeur (v37) */
export function isUndefLiteral(t: Term): boolean {
  return t.k === 'div' && isNumT(t.b) && Q.isZero(t.b.v) && ratValue(t.a) !== null;
}

/** un membre sans lettre qui divise par 0 quelque part (« (2 + 1) ÷ (2 − 2) ») */
function divisesByZero(t: Term): boolean {
  switch (t.k) {
    case 'div':
      return (
        constSameValue(t.b, { k: 'num', v: Q.ZERO }) === true ||
        divisesByZero(t.a) ||
        divisesByZero(t.b)
      );
    case 'add':
    case 'mul':
      return t.c.some(divisesByZero);
    case 'pow':
    case 'binom':
      return divisesByZero(t.a) || divisesByZero(t.b);
    case 'fn':
      return divisesByZero(t.a);
    default:
      return false;
  }
}

/** moteur v45 : un logarithme d'un nombre négatif ou nul écrit (« ln(−4) », « ln(0) ») */
function lnUndefined(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      if (t.f === 'ln' && isConst(t.a)) {
        const z: Term = { k: 'num', v: Q.ZERO };
        if (constCmpHolds('≤', t.a, z) === true) return true;
      }
      return lnUndefined(t.a);
    case 'add':
    case 'mul':
      return t.c.some(lnUndefined);
    case 'div':
    case 'pow':
    case 'binom':
      return lnUndefined(t.a) || lnUndefined(t.b);
    default:
      return false;
  }
}

/** deux écritures d'une même division par 0 : numérateurs égaux, dénominateurs nuls */
function sameUndefined(a: Term, b: Term): boolean {
  return (
    a.k === 'div' &&
    b.k === 'div' &&
    divisesByZero(a) &&
    divisesByZero(b) &&
    constSameValue(a.b, { k: 'num', v: Q.ZERO }) === true &&
    constSameValue(b.b, { k: 'num', v: Q.ZERO }) === true &&
    constSameValue(a.a, b.a) === true
  );
}

export function isClaimValue(t: Term): boolean {
  if (!isConst(t)) return false;
  if (ratValue(t) !== null) return true;
  if (constOps(t) !== 0) return false;
  if (reducibleRoot(t)) return false;
  // moteur v45 : ln(8), ln(1 ÷ 4), ln(√2), ln(e) se simplifient encore
  if (reducibleLn(t)) return false;
  const n = normalForm(t);
  return n.ok;
}

/* ------------------------------ état ------------------------------ */

function relOf(text: string, e = true): { rel: Rel; l: Term; r: Term } | null {
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'rel') return null;
  return e
    ? { rel: p.s.rel, l: withE(p.s.l), r: withE(p.s.r) }
    : { rel: p.s.rel, l: p.s.l, r: p.s.r };
}

/**
 * la vérité exacte d'une relation entre membres sans lettre (null : indécis) ; avec des
 * lettres (moteur v50), une égalité est vraie si elle l'est pour toutes les valeurs
 */
function relHolds(rel: Rel, l: Term, r: Term): boolean | null {
  if (!isConst(l) || !isConst(r)) return rel === '=' ? letterSame(l, r) : null;
  if (rel === '=') return constSameValue(l, r);
  return constCmpHolds(rel, l, r);
}

/**
 * L'affirmation dont une ligne remplace le membre à lettres `ms[0]`. Une seule l'écrit :
 * c'est elle (comme avant le moteur v39). Plusieurs (« 4x − 7 ≥ 5 » pour x = 3, x = 2…) :
 * celle dont la valeur proposée donne le membre suivant de la ligne (« 4 × 3 − 7 »), une
 * pas encore remplacée d'abord ; sinon la dernière.
 */
function claimOfLetteredLine(ks: KClaimsState, ms: readonly Term[]): number | undefined {
  const k = key(ms[0]!);
  const all = ks.rawSidesAll.get(k);
  if (!all || all.length < 2 || ms.length < 2) return ks.rawSides.get(k);
  const fits = all.filter((idx) => {
    const sub = substitute(ms[0]!, ks.claims[idx]!.at ?? {});
    return key(sub) === key(ms[1]!) || constStep(sub, ms[1]!) === 'same';
  });
  return fits.find((idx) => !ks.replaced.has(idx)) ?? fits[0] ?? ks.rawSides.get(k);
}

export function createKClaimsState(model: ScratchModel): KClaimsState {
  const spec = model.kernel!;
  const specs = spec.claims ?? [];
  if (specs.length < 2) throw new ClaimsModelError('au moins deux affirmations');
  const eConst = !specs.some((c) => c.at && 'e' in c.at);
  const rawSides = new Map<string, number>();
  const rawSidesAll = new Map<string, number[]>();
  const addRaw = (k: string, index: number) => {
    rawSides.set(k, index);
    const all = rawSidesAll.get(k) ?? [];
    if (!all.includes(index)) all.push(index);
    rawSidesAll.set(k, all);
  };
  const claims: KClaim[] = specs.map((c, index) => {
    let at: Record<string, Term> | null = null;
    if (c.at && Object.keys(c.at).length) {
      at = {};
      for (const [n, v] of Object.entries(c.at)) {
        const e = exprOf(v, eConst);
        if (!e || !isConst(e)) throw new ClaimsModelError(`valeur de ${n} illisible : ${v}`);
        at[n] = e;
      }
    }
    const isProp = c.rels.length === 1 && !/[=<>≤≥≠]/.test(c.rels[0]!);
    if (isProp) {
      const text = c.rels[0]!;
      const raw = exprOf(text, eConst);
      if (!raw) throw new ClaimsModelError(`écriture illisible : ${text}`);
      const term = at ? substitute(raw, at) : raw;
      if (!isConst(term)) throw new ClaimsModelError(`lettre sans valeur dans « ${text} »`);
      if (!isConst(raw)) addRaw(key(raw), index);
      const prop: ClaimProp | null = c.divisibleBy
        ? { kind: 'divisible', d: BigInt(c.divisibleBy) }
        : c.form
          ? { kind: 'form', form: c.form }
          : null;
      if (!prop) throw new ClaimsModelError(`propriété absente : ${text}`);
      let truth: boolean;
      if (prop.kind === 'divisible' || prop.form === 'prime') {
        const n = intValueOf(term);
        if (n === null)
          throw new ClaimsModelError(
            `« ${prop.kind === 'divisible' ? 'divisible par' : 'nombre premier'} » sur un entier : ${text}`,
          );
        truth = prop.kind === 'divisible' ? n % prop.d === 0n : isPrimeInt(n);
      } else
        truth =
          prop.form === 'irreducible' ? irreducibleTruth(term) : hasClaimForm(term, prop.form);
      const negated = !!c.not;
      if (negated) truth = !truth;
      const atText = c.at
        ? Object.entries(c.at)
            .map(([n, v]) => `${n} = ${v}`)
            .join(' ; ')
        : null;
      return {
        index,
        rels: [{ rel: '=' as Rel, l: term, r: term, rawL: raw, rawR: raw, text }],
        at,
        atText,
        truth,
        form: null,
        formOk: true,
        prop,
        negated,
      };
    }
    // moteur v50 : une identité en lettres (aucune valeur proposée)
    const identity =
      !at &&
      c.rels.some((text) => {
        const r = relOf(text, eConst);
        return !!r && (hasLetters(r.l) || hasLetters(r.r));
      });
    if (identity && c.form)
      throw new ClaimsModelError('une forme va avec une affirmation sans lettre');
    const rels: ClaimRel[] = c.rels.map((text) => {
      const r = relOf(text, eConst);
      if (!r) throw new ClaimsModelError(`affirmation illisible : ${text}`);
      const l = at ? substitute(r.l, at) : r.l;
      const rr = at ? substitute(r.r, at) : r.r;
      if (identity) {
        if (r.rel !== '=')
          throw new ClaimsModelError(`une identité en lettres est une égalité : ${text}`);
        return { rel: r.rel, l, r: rr, rawL: r.l, rawR: r.r, text };
      }
      if (!isConst(l) || !isConst(rr))
        throw new ClaimsModelError(`lettre sans valeur dans « ${text} »`);
      if (!isConst(r.l)) addRaw(key(r.l), index);
      if (!isConst(r.r)) addRaw(key(r.r), index);
      return { rel: r.rel, l, r: rr, rawL: r.l, rawR: r.r, text };
    });
    const form: ClaimForm | null = c.form ?? null;
    if (form && (rels.length !== 1 || rels[0]!.rel !== '='))
      throw new ClaimsModelError('une forme va avec UNE égalité « a = b »');
    const formOk = form ? hasClaimForm(rels[0]!.rawR, form) : true;
    let truth = formOk;
    for (const r of rels) {
      let h = relHolds(r.rel, r.l, r.r);
      // valeur interdite (v37) : un membre qui divise par 0 n'existe pas, l'affirmation est fausse
      if (h === null && (divisesByZero(r.l) || divisesByZero(r.r))) h = false;
      // moteur v45 : ln(−4) n'existe pas — l'affirmation est fausse
      if (h === null && (lnUndefined(r.l) || lnUndefined(r.r))) h = false;
      if (h === null) throw new ClaimsModelError(`affirmation indécidable : ${r.text}`);
      if (!h) truth = false;
    }
    const atText = c.at
      ? Object.entries(c.at)
          .map(([n, v]) => `${n} = ${v}`)
          .join(' ; ')
      : null;
    return {
      index,
      rels,
      at,
      atText,
      truth,
      form,
      formOk,
      prop: null,
      negated: false,
      ...(identity ? { identity: true } : {}),
    };
  });
  const letters = claims.some((c) => c.identity);
  if (letters && claims.some((c) => c.at))
    throw new ClaimsModelError(
      'des identités en lettres et des valeurs proposées : pas sur la même feuille',
    );
  const known = new Set<string>();
  for (const c of claims)
    for (const r of c.rels) {
      // un membre qui est déjà une valeur n'est pas un calcul à faire, mais une source
      known.add(key(r.l));
      known.add(key(r.r));
    }
  const traps = (model.traps ?? []).flatMap((t) => {
    const r = relOf(t.line, eConst);
    if (!r) return [];
    let line: Line;
    try {
      line = parseText(t.line);
    } catch {
      return [];
    }
    const k1 = `${key(r.l)}=${key(r.r)}`;
    const k2 = `${key(r.r)}=${key(r.l)}`;
    return [{ line, title: t.title, why: t.why, keys: [k1, k2] as [string, string] }];
  });
  return {
    model,
    claims,
    parent: new Map(),
    value: new Map(),
    terms: new Map(),
    known,
    rawSides,
    rawSidesAll,
    single: !!spec.single,
    eConst,
    letters,
    replaced: new Set(),
    written: new Set(),
    path: spec.path ?? [],
    traps,
    lines: 0,
  };
}

/* ------------------------------ classes ------------------------------ */

function find(parent: Map<string, string>, k: string): string {
  let x = k;
  while (parent.has(x) && parent.get(x) !== x) x = parent.get(x)!;
  return x;
}

interface Classes {
  parent: Map<string, string>;
  value: Map<string, Term>;
  terms: Map<string, Term>;
  /** moteur v50 : une écriture développée et réduite en lettres est une valeur */
  letters?: boolean;
}

/** une valeur de la feuille : un nombre, une valeur interdite, ou (v50) une écriture
 *  développée et réduite en lettres */
function isValueOf(c: Classes, t: Term): boolean {
  return isClaimValue(t) || isUndefLiteral(t) || (!!c.letters && isLetterValue(t));
}

function union(c: Classes, a: string, b: string): void {
  const ra = find(c.parent, a);
  const rb = find(c.parent, b);
  if (ra === rb) return;
  c.parent.set(ra, rb);
  if (!c.parent.has(rb)) c.parent.set(rb, rb);
  const va = c.value.get(ra);
  if (va && !c.value.has(rb)) c.value.set(rb, va);
}

function addChain(c: Classes, keys: string[], terms: Term[]): void {
  keys.forEach((k, i) => {
    if (!c.parent.has(k)) c.parent.set(k, k);
    const t = terms[i]!;
    if (!c.terms.has(k)) c.terms.set(k, t);
    if (isValueOf(c, t)) {
      const r = find(c.parent, k);
      if (!c.value.has(r)) c.value.set(r, t);
    }
  });
  for (let i = 1; i < keys.length; i++) union(c, keys[0]!, keys[i]!);
}

/** la valeur écrite d'un membre : lui-même si c'est une valeur, sinon celle de sa classe */
function valueIn(c: Classes, t: Term): Term | null {
  if (isValueOf(c, t)) return t;
  const k = key(t);
  if (!c.parent.has(k)) return null;
  return c.value.get(find(c.parent, k)) ?? null;
}

/** la vérité d'une relation d'après ce qui est écrit (null : pas encore tranchée) */
function relDecideIn(c: Classes, r: ClaimRel): boolean | null {
  const kl = key(r.l);
  const kr = key(r.r);
  // une relation se tranche par ce qui est ÉCRIT : un de ses membres doit être sur la feuille
  if (!c.parent.has(kl) && !c.parent.has(kr)) return null;
  if (
    r.rel === '=' &&
    c.parent.has(kl) &&
    c.parent.has(kr) &&
    find(c.parent, kl) === find(c.parent, kr)
  )
    return true;
  // moteur v45 : « ln(−4) » n'existe pas — la relation est fausse dès que l'autre membre est écrit
  if ((c.parent.has(kl) && lnUndefined(r.r)) || (c.parent.has(kr) && lnUndefined(r.l)))
    return false;
  const vl = valueIn(c, r.l);
  const vr = valueIn(c, r.r);
  // « 3 ÷ 0 » écrit : le membre n'existe pas, la relation est fausse (valeur interdite)
  if (vl && vr) return isUndefLiteral(vl) || isUndefLiteral(vr) ? false : relHolds(r.rel, vl, vr);
  return null;
}

/**
 * Une affirmation avec une FORME (moteur v39) : dès que le membre de gauche est écrit
 * égal à une écriture qui a cette forme (« 36 ÷ 48 = 3 ÷ 4 », « 84 = 2² × 3 × 7 »), la
 * forme est unique : l'affirmation est vraie si son membre de droite a la forme ET la même
 * valeur. Sinon, comme une égalité : fausse si l'égalité l'est, ou si elle est vraie mais
 * le membre de droite n'a pas la forme.
 */
function formDecideIn(c: Classes, claim: KClaim): boolean | null {
  const r = claim.rels[0]!;
  const kl = key(r.l);
  if (c.parent.has(kl)) {
    const root = find(c.parent, kl);
    for (const [k, t] of c.terms)
      if (find(c.parent, k) === root && hasClaimForm(t, claim.form!))
        return claim.formOk && constSameValue(t, r.r) === true;
  }
  const h = relDecideIn(c, r);
  return h === null ? null : h && claim.formOk;
}

/** les écritures de la feuille de la même classe que t (t compris s'il est écrit) */
function writtenWith(c: Classes, t: Term): Term[] {
  const k = key(t);
  if (!c.parent.has(k)) return [];
  const root = find(c.parent, k);
  const out: Term[] = [];
  for (const [kk, tt] of c.terms) if (find(c.parent, kk) === root) out.push(tt);
  return out;
}

/**
 * Une affirmation de PROPRIÉTÉ (moteur v40), d'après ce qui est écrit :
 * — nombre premier : fausse dès qu'un produit de deux entiers plus grands que 1 est écrit
 *   égal (« 91 = 7 × 13 ») ; vraie quand la division par chaque nombre premier jusqu'à √n
 *   laisse un reste (« 29 = 2 × 14 + 1 », « 29 = 3 × 9 + 2 », « 29 = 5 × 5 + 4 ») ;
 * — divisible par d : vraie dès que « d × q » est écrit égal, fausse dès qu'une division
 *   par d laisse un reste (« 4183 = 6 × 697 + 1 ») ;
 * — une forme (fraction irréductible, notation scientifique, facteurs premiers) : la forme
 *   est unique — dès qu'une écriture égale dans la forme est sur la feuille, l'affirmation est
 *   vraie si c'est la même écriture, fausse sinon (« 6 ÷ 9 = 2 ÷ 3 »).
 */
function propDecideIn(c: Classes, claim: KClaim): boolean | null {
  const p = claim.prop!;
  const t = claim.rels[0]!.l;
  const ws = writtenWith(c, t);
  if (!ws.length) return null;
  let h: boolean | null = null;
  if (p.kind === 'divisible') {
    for (const w of ws) {
      const e = euclidParts(w);
      if (!e || !e.factors.includes(p.d)) continue;
      if (e.r === 0n) h = true;
      else if (e.r < p.d) h = false;
      if (h !== null) break;
    }
  } else if (p.form === 'prime') {
    const n = intValueOf(t);
    if (n !== null && n >= 2n) {
      const composite = ws.some((w) => {
        const e = euclidParts(w);
        return !!e && e.r === 0n && e.factors.every((f) => f >= 2n);
      });
      if (composite) h = false;
      else {
        const tested = (q: bigint) =>
          ws.some((w) => {
            const e = euclidParts(w);
            return !!e && e.r > 0n && e.r < q && e.factors.includes(q);
          });
        let all = true;
        for (let q = 2n; q * q <= n && all; q++) if (isPrimeInt(q) && !tested(q)) all = false;
        if (all) h = true;
      }
    }
  } else {
    const raw = claim.rels[0]!.rawR;
    const shown = claim.at ? t : raw;
    // moteur v53 : une fraction écrite avec ses facteurs (« 2² ÷ (3 × 5) ») se compare par la
    // valeur de son numérateur et de son dénominateur (4 ÷ 15)
    const ff = p.form === 'irreducible' ? factoredFraction(shown) : null;
    for (const w of ws)
      if (hasClaimForm(w, p.form)) {
        h = ff
          ? sameFractionValues(w, ff)
          : sameFormWriting(w, shown, p.form) && hasClaimForm(shown, p.form);
        break;
      }
  }
  return h === null ? null : claim.negated ? !h : h;
}

/** la vérité d'une affirmation d'après ce qui est écrit (null : pas encore tranchée) */
function decideIn(c: Classes, claim: KClaim): boolean | null {
  if (claim.prop) return propDecideIn(c, claim);
  if (claim.form) return formDecideIn(c, claim);
  let all = true;
  for (const r of claim.rels) {
    const h = relDecideIn(c, r);
    if (h === false) return false;
    if (h === null) all = false;
  }
  return all ? true : null;
}

const classesOf = (ks: KClaimsState): Classes => ({
  parent: ks.parent,
  value: ks.value,
  terms: ks.terms,
  letters: ks.letters,
});
function cloneClasses(ks: KClaimsState): Classes {
  return {
    parent: new Map(ks.parent),
    value: new Map(ks.value),
    terms: new Map(ks.terms),
    letters: ks.letters,
  };
}

/**
 * QCM à réponse unique (moteur v40) : une affirmation vraie trouvée, les autres sont
 * fausses ; toutes fausses sauf une, elle est vraie.
 */
function eliminate(ks: KClaimsState, ds: (boolean | null)[]): (boolean | null)[] {
  if (!ks.single) return ds;
  if (ds.filter((d) => d === true).length === 1) return ds.map((d) => d ?? false);
  const open = ds.filter((d) => d === null).length;
  if (open === 1 && ds.every((d) => d !== true)) return ds.map((d) => d ?? true);
  return ds;
}

/** la décision de chaque affirmation (dans l'ordre des choix) */
export function kclaimsDecisions(ks: KClaimsState): (boolean | null)[] {
  const c = classesOf(ks);
  return eliminate(
    ks,
    ks.claims.map((cl) => decideIn(c, cl)),
  );
}

export function kclaimsGoalReached(ks: KClaimsState): boolean {
  return kclaimsDecisions(ks).every((d) => d !== null);
}

/* ------------------------------ pas particuliers ------------------------------ */

/** parenthèses d'un nombre relatif écrites : « (−3) », « (+10) » */
function signParens(atoms: string[]): number {
  let n = 0;
  for (let i = 0; i + 1 < atoms.length; i++)
    if (atoms[i] === '(' && (atoms[i + 1] === '−' || atoms[i + 1] === '+')) n++;
  return n;
}

/** « aᵐ » → base et exposant (une base seule : exposant 1) */
function powParts(t: Term): { base: string; e: Term; b: Term } | null {
  if (t.k === 'pow') return { base: key(t.a), e: t.b, b: t.a };
  if (isNumT(t) || t.k === 'sym') return { base: key(t), e: { k: 'num', v: Q.ONE }, b: t };
  return null;
}
/** valeur exacte d'un exposant écrit (« 2 + 3 », « −2 », « 2 × 3 ») */
function expValue(t: Term): Q.Rat | null {
  return evalRat(t);
}
function evalRat(t: Term): Q.Rat | null {
  switch (t.k) {
    case 'num':
      return t.v;
    case 'add': {
      let s = Q.ZERO;
      for (const c of t.c) {
        const v = evalRat(c);
        if (!v) return null;
        s = Q.add(s, v);
      }
      return s;
    }
    case 'mul': {
      let s = Q.ONE;
      for (const c of t.c) {
        const v = evalRat(c);
        if (!v) return null;
        s = Q.mul(s, v);
      }
      return s;
    }
    case 'div': {
      const a = evalRat(t.a);
      const b = evalRat(t.b);
      if (!a || !b || Q.isZero(b)) return null;
      return Q.div(a, b);
    }
    default:
      return null;
  }
}

function powerStep(a: Term, b: Term): StepKind | null {
  // a⁻ⁿ → 1 ÷ aⁿ
  if (a.k === 'pow' && b.k === 'div') {
    const n = expValue(a.b);
    const one = intOf(b.a);
    if (n && Q.sign(n) < 0 && one === 1n && b.b.k === 'pow' && key(b.b.a) === key(a.a)) {
      const m = expValue(b.b.b);
      if (m && Q.eq(m, Q.neg(n))) return 'powerNeg';
    }
  }
  // (aᵐ)ⁿ → aᵐˣⁿ
  if (a.k === 'pow' && a.a.k === 'pow' && b.k === 'pow' && key(b.a) === key(a.a.a)) {
    const m = expValue(a.a.b);
    const n = expValue(a.b);
    const e = expValue(b.b);
    if (m && n && e && Q.eq(Q.mul(m, n), e)) return 'powerPower';
  }
  // aᵐ × aⁿ → aᵐ⁺ⁿ (deux facteurs de même base)
  if (a.k === 'mul' && a.c.length === 2 && b.k === 'pow') {
    const p = powParts(a.c[0]!);
    const q = powParts(a.c[1]!);
    if (p && q && p.base === q.base && p.base === key(b.a)) {
      const m = expValue(p.e);
      const n = expValue(q.e);
      const e = expValue(b.b);
      if (m && n && e && Q.eq(Q.add(m, n), e) && (a.c[0]!.k === 'pow' || a.c[1]!.k === 'pow'))
        return 'powerProduct';
    }
  }
  // aᵐ ÷ aⁿ → aᵐ⁻ⁿ
  if (a.k === 'div' && b.k === 'pow') {
    const p = powParts(a.a);
    const q = powParts(a.b);
    if (p && q && p.base === q.base && p.base === key(b.a)) {
      const m = expValue(p.e);
      const n = expValue(q.e);
      const e = expValue(b.b);
      if (m && n && e && Q.eq(Q.sub(m, n), e) && (a.a.k === 'pow' || a.b.k === 'pow'))
        return 'powerQuotient';
    }
  }
  return null;
}

/** facteurs d'un produit, à plat */
function mulFactors(t: Term): Term[] {
  return t.k === 'mul' ? t.c.flatMap(mulFactors) : [t];
}

/** aⁿ → a × a × … × a (n entier de 2 à 8, n facteurs égaux à a) */
function powerExpands(a: Term, b: Term): boolean {
  if (a.k !== 'pow' || b.k !== 'mul') return false;
  const n = intOf(a.b);
  if (n === null || n < 2n || n > 8n) return false;
  const fs = mulFactors(b);
  const base = key(a.a);
  return fs.length === Number(n) && fs.every((f) => key(f) === base);
}

/**
 * Moteur v41 (refonte lot 6.17) : un maillon que constStep juge trop grand montre-t-il
 * un geste sur les puissances ? « 0,2³ → 0,2 × 0,2 × 0,2 » (écrire la puissance en
 * produit), ou une règle des puissances appliquée À UN SEUL ENDROIT de l'écriture :
 * « (5⁴ × 5²) ÷ 5³ → 5⁶ ÷ 5³ ». Le reste de l'écriture est recopié tel quel.
 */
export function powerGesture41(a: Term, b: Term): StepKind | null {
  if (powerExpands(a, b)) return 'powerExpand';
  const top = powerStep(a, b);
  if (top) return top;
  if (a.k !== b.k) return null;
  if (a.k === 'div' || a.k === 'pow') {
    const bb = b as typeof a;
    if (key(a.a) === key(bb.a)) return powerGesture41(a.b, bb.b);
    if (key(a.b) === key(bb.b)) return powerGesture41(a.a, bb.a);
    return null;
  }
  if ((a.k === 'mul' || a.k === 'add') && b.k === a.k && a.c.length === b.c.length) {
    const diff = a.c.flatMap((c, i) => (key(c) === key(b.c[i]!) ? [] : [i]));
    return diff.length === 1 ? powerGesture41(a.c[diff[0]!]!, b.c[diff[0]!]!) : null;
  }
  return null;
}

/**
 * Le corrigé d'une feuille d'affirmations fait-il un geste du moteur v41 (écrire une
 * puissance en produit, une règle des puissances à un endroit) ? Une telle feuille est en
 * v41.
 */
export function pathUsesClaimsPower41(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map((t) => {
      const p = parseStatement(t.trim());
      return p.ok && p.s.k === 'expr' ? p.s.e : null;
    });
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++) {
      const a = ms[i]!;
      const b = ms[i + 1]!;
      // une règle des puissances sur toute l'écriture : une app v40 la juge déjà
      if (constStep(a, b) === 'jump' && !powerStep(a, b) && powerGesture41(a, b)) return true;
    }
  }
  return false;
}

/** « k × p » (k et p entiers, dans un ordre ou l'autre) */
function factored(t: Term): { k: bigint; x: bigint }[] {
  if (t.k !== 'mul' || t.c.length !== 2) return [];
  const a = intOf(t.c[0]!);
  const b = intOf(t.c[1]!);
  if (a === null || b === null) return [];
  return [
    { k: a, x: b },
    { k: b, x: a },
  ];
}

/** un terme d'une somme : fraction (avec son signe) ou entier */
function sumFractions(t: Term): { p: bigint; q: bigint; written: boolean }[] | null {
  if (t.k !== 'add') return null;
  const out: { p: bigint; q: bigint; written: boolean }[] = [];
  for (const c of t.c) {
    const f = fracOf(c);
    if (f) {
      out.push({ ...f, written: true });
      continue;
    }
    const n = intOf(c);
    if (n === null) return null;
    out.push({ p: n, q: 1n, written: false });
  }
  return out;
}

function fractionStep(a: Term, b: Term): StepKind | null {
  const fa = fracOf(a);
  const fb = fracOf(b);
  // simplifier : p ÷ q → p' ÷ q' (même valeur, dénominateur plus petit)
  if (fa && fb && fb.q !== 0n) {
    const absQ = (x: bigint) => (x < 0n ? -x : x);
    if (fa.p * fb.q === fb.p * fa.q && absQ(fb.q) < absQ(fa.q)) return 'fracSimplify';
  }
  // facteur commun : p ÷ q → (k × p') ÷ (k × q')
  if (fa && b.k === 'div') {
    for (const n of factored(b.a))
      for (const d of factored(b.b))
        if (n.k === d.k && n.k > 1n && n.k * n.x === fa.p && d.k * d.x === fa.q)
          return 'fracFactor';
  }
  // simplifier par le facteur commun : (k × p') ÷ (k × q') → p' ÷ q' (ou p')
  if (a.k === 'div') {
    for (const n of factored(a.a))
      for (const d of factored(a.b)) {
        if (n.k !== d.k || n.k <= 1n) continue;
        if (fb && fb.p === n.x && fb.q === d.x) return 'fracSimplify';
        const ib = intOf(b);
        if (d.x === 1n && ib !== null && ib === n.x) return 'fracSimplify';
      }
  }
  // même dénominateur : Σ pᵢ ÷ qᵢ → Σ cᵢ ÷ d (chaque terme garde sa valeur)
  const sa = sumFractions(a);
  const sb = sumFractions(b);
  if (sa && sb && sa.length === sb.length && sb.length >= 2) {
    const d = sb[0]!.q;
    const sameD = sb.every((x) => x.q === d && x.written) && d !== 1n;
    const keep = sa.every((x, i) => x.p * sb[i]!.q === sb[i]!.p * x.q);
    const changed = sa.some((x, i) => x.q !== sb[i]!.q);
    if (sameD && keep && changed) return 'fracCommon';
  }
  // additionner les numérateurs : Σ pᵢ ÷ d → (Σ pᵢ) ÷ d
  if (sa && sa.length >= 2 && b.k === 'div') {
    const d = sa[0]!.q;
    if (d !== 1n && sa.every((x) => x.q === d && x.written)) {
      const total = sa.reduce((s, x) => s + x.p, 0n);
      const den = intOf(b.b);
      if (den === d) {
        const nb = intOf(b.a);
        if (nb !== null && nb === total) return 'fracAdd';
        if (b.a.k === 'add') {
          const parts = b.a.c.map(intOf);
          if (parts.every((x) => x !== null) && parts.reduce((s, x) => s + x!, 0n) === total)
            return 'fracAdd';
        }
      }
    }
  }
  // multiplier les fractions : (p ÷ q) × (r ÷ s) → (p × r) ÷ (q × s), ou pr ÷ qs
  if (a.k === 'mul' && a.c.length >= 2 && b.k === 'div') {
    const fs = a.c.map((c) => fracOf(c) ?? (intOf(c) !== null ? { p: intOf(c)!, q: 1n } : null));
    if (fs.every((x) => x !== null) && a.c.some((c) => c.k === 'div')) {
      const P = fs.reduce((s, x) => s * x!.p, 1n);
      const D = fs.reduce((s, x) => s * x!.q, 1n);
      const prodOf = (t: Term): bigint | null => {
        const i = intOf(t);
        if (i !== null) return i;
        if (t.k === 'mul') {
          const xs = t.c.map(intOf);
          return xs.every((x) => x !== null) ? xs.reduce((s, x) => s * x!, 1n) : null;
        }
        return null;
      };
      if (prodOf(b.a) === P && prodOf(b.b) === D) return 'fracMul';
    }
  }
  // multiplier par l'inverse : x ÷ (p ÷ q) → x × (q ÷ p)
  if (a.k === 'div' && b.k === 'mul' && b.c.length === 2) {
    const f = fracOf(a.b);
    if (f) {
      const [u, v] = b.c as [Term, Term];
      const inv = (t: Term) => {
        const g = fracOf(t);
        return !!g && g.p === f.q && g.q === f.p;
      };
      if ((key(u) === key(a.a) && inv(v)) || (key(v) === key(a.a) && inv(u))) return 'fracInverse';
    }
  }
  return null;
}

/* ------------------------------ juge ------------------------------ */

type Base = { atoms: string[]; line: Line };

/** atomes de chaque membre d'une chaîne « a = b = c » */
function memberAtoms(A: string[]): string[][] {
  const out: string[][] = [[]];
  let depth = 0;
  for (const a of A) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (a === '=' && depth === 0) out.push([]);
    else out[out.length - 1]!.push(a);
  }
  return out;
}

function knownPrefix(ks: KClaimsState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = kclaimsLineAtoms(t);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function kclaimsJudge(
  ks: KClaimsState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KClaimsVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownPrefix(ks, A);
  const syn = checkSyntax(A);
  if (!syn.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  }
  if (!syn.complete || !syn.hasEq)
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  const F0 = kforReadAtoms(A);
  const F = F0 && withEMembers(ks, F0);
  if (!F) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Une relation par ligne',
      message: 'Écris un calcul avec « = » (« 11 × 48 = … »), ou une comparaison.',
    };
  }
  const v = evaluate(ks, F, A);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

const quoted = (t: Term) => `« ${formulaText(t)} »`;

type ReadLine = NonNullable<ReturnType<typeof kforReadAtoms>>;
/** la ligne lue, la constante e en exp(1) ; `raw` garde les membres tels qu'écrits */
function withEMembers(ks: KClaimsState, F: ReadLine): ReadLine & { raw: Term[] } {
  if (!ks.eConst) return { ...F, raw: F.members };
  return { ...F, members: F.members.map(withE), raw: F.members };
}

/** Le texte écrit de chaque membre d'une chaîne « = » (au niveau 0 des parenthèses). */
function rawMembers(A: string[]): string[] {
  const out: string[][] = [[]];
  let depth = 0;
  for (const a of A) {
    if (a === '(' || a === '[') depth++;
    if (a === ')' || a === ']') depth--;
    if (a === '=' && depth === 0) out.push([]);
    else out[out.length - 1]!.push(a);
  }
  return out.map((m) => m.join(' '));
}

const FORM_WORDS: Record<ClaimForm, string> = {
  irreducible: 'fraction irréductible',
  scientific: 'notation scientifique',
  primes: 'en facteurs premiers',
  prime: 'nombre premier',
};
/** la propriété d'une écriture seule (moteur v40), et sa négation */
const PROP_WORDS: Record<ClaimForm, [string, string]> = {
  irreducible: ['fraction irréductible', 'pas une fraction irréductible'],
  scientific: ['notation scientifique', 'pas en notation scientifique'],
  primes: ['décomposition en facteurs premiers', 'pas une décomposition en facteurs premiers'],
  prime: ['nombre premier', 'pas un nombre premier'],
};
function propWords(p: ClaimProp, negated: boolean): string {
  if (p.kind === 'divisible') return `${negated ? 'pas divisible' : 'divisible'} par ${p.d}`;
  return PROP_WORDS[p.form][negated ? 1 : 0];
}
/**
 * L'étiquette d'une affirmation, affichée après elle (« fraction irréductible »,
 * « nombre premier », « divisible par 6 », « pas un nombre premier ») ; null sans forme
 * ni propriété.
 */
export function kclaimTag(c: {
  rels: readonly string[];
  form?: ClaimForm;
  divisibleBy?: string;
  divisors?: { of: 'count' | 'sum' | 'product'; value: string; proper?: true };
  not?: boolean;
}): string | null {
  const prop = c.rels.length === 1 && !/[=<>≤≥≠]/.test(c.rels[0]!);
  if (prop) {
    if (c.divisors) {
      // moteur v2 (30/10) : « 6 diviseurs », « somme des diviseurs (sauf 28) : 28 »
      const d = c.divisors;
      if (d.of === 'count') return `${d.value} diviseur${d.value === '1' ? '' : 's'}`;
      const sans = d.proper ? ` (sauf ${c.rels[0]})` : '';
      return `${d.of === 'sum' ? 'somme' : 'produit'} des diviseurs${sans} : ${d.value}`;
    }
    if (c.divisibleBy) return propWords({ kind: 'divisible', d: BigInt(c.divisibleBy) }, !!c.not);
    if (c.form) return propWords({ kind: 'form', form: c.form }, !!c.not);
    return null;
  }
  return c.form ? FORM_WORDS[c.form] : null;
}
/** pourquoi une écriture n'a pas la forme demandée */
const FORM_WHY: Record<ClaimForm, string> = {
  irreducible: 'Cette écriture n’est pas une fraction irréductible : elle se simplifie encore.',
  scientific:
    'Ce n’est pas une notation scientifique : le nombre devant la puissance de 10 doit être au moins 1 et moins que 10.',
  primes:
    'Ce n’est pas une décomposition en facteurs premiers : chaque facteur doit être premier, et écrit une seule fois (avec une puissance s’il revient).',
  prime: '',
};
/** pourquoi une affirmation de propriété est tranchée (moteur v40) */
function propWhy(c: KClaim, truth: boolean): string {
  const p = c.prop!;
  const has = c.negated ? !truth : truth;
  if (p.kind === 'divisible')
    return has
      ? `La division par ${p.d} tombe juste : le reste est 0.`
      : `La division par ${p.d} laisse un reste : il n’est pas 0.`;
  if (p.form === 'prime')
    return has
      ? 'Aucun nombre premier jusqu’à sa racine carrée ne le divise : il est premier.'
      : 'Il s’écrit comme un produit de deux entiers plus grands que 1 : il n’est pas premier.';
  return has ? '' : FORM_WHY[p.form];
}
function claimText(c: KClaim): string {
  const t = c.rels.map((r) => r.text).join(' et ');
  if (c.prop) {
    const w = propWords(c.prop, c.negated);
    return c.atText ? `${t} (${w}, pour ${c.atText})` : `${t} (${w})`;
  }
  if (c.form) return `${t} (${FORM_WORDS[c.form]})`;
  return c.atText ? `${t} (pour ${c.atText})` : t;
}

/**
 * Les mots d'une décision. `raw` : les décisions SANS élimination — une affirmation tranchée
 * seulement par élimination (QCM à réponse unique, moteur v40) le dit.
 */
function decisionWords(
  ks: KClaimsState,
  decided: { index: number; truth: boolean }[],
  raw?: (boolean | null)[],
): string {
  // les affirmations tranchées par la ligne d'abord, celles qui en découlent par élimination
  // ensuite (moteur v40), regroupées en une phrase
  const elim = (d: { index: number }) => !!raw && raw[d.index] === null;
  const direct = decided
    .filter((d) => !elim(d))
    .map((d) => {
      const c = ks.claims[d.index]!;
      const zero = !d.truth && c.rels.some((r) => divisesByZero(r.l) || divisesByZero(r.r));
      const form = !d.truth && c.form && !c.formOk ? ' ' + FORM_WHY[c.form] : '';
      const prop = c.prop ? propWhy(c, d.truth) : '';
      return `« ${claimText(c)} » est ${d.truth ? 'VRAIE' : 'FAUSSE'}.${zero ? ' On ne divise jamais par 0 : valeur interdite.' : ''}${form}${prop ? ' ' + prop : ''}`;
    });
  const quoted = (ds: { index: number }[]) => {
    const q = ds.map((d) => `« ${claimText(ks.claims[d.index]!)} »`);
    return q.length > 1 ? `${q.slice(0, -1).join(', ')} et ${q[q.length - 1]}` : q[0]!;
  };
  const viaTrue = decided.filter((d) => elim(d) && d.truth);
  const viaFalse = decided.filter((d) => elim(d) && !d.truth);
  if (viaTrue.length)
    direct.push(
      `Une seule réponse est juste et les autres sont fausses : ${quoted(viaTrue)} est donc VRAIE.`,
    );
  if (viaFalse.length)
    direct.push(
      `Une seule réponse est juste, et elle est trouvée : ${quoted(viaFalse)} ${
        viaFalse.length > 1 ? 'sont donc FAUSSES' : 'est donc FAUSSE'
      }.`,
    );
  return direct.join(' ');
}

function evaluate(
  ks: KClaimsState,
  F: {
    members: Term[];
    cmp: '>' | '<' | '≥' | '≤' | null;
    approx: Term | null;
    line: Line;
    raw?: Term[];
  },
  A: string[],
): KClaimsVerdict {
  const base: Base = { atoms: A, line: F.line };
  // les membres tels qu'écrits (« e », pas exp(1)), pour les messages
  const shown = F.raw ?? F.members;
  if (F.approx)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Des valeurs exactes',
      message: 'Ici, on tranche avec des calculs exacts : écris « = » et la valeur exacte.',
    };
  const ms = F.members;

  // des lettres : seulement une affirmation écrite telle quelle, suivie de son remplacement
  // (sauf des identités en lettres, moteur v50 : on calcule avec les lettres)
  const lettered = ms.map((m) => !isConst(m));
  if (
    !ks.letters &&
    (lettered.some((x, i) => x && i > 0) || (lettered[0] && (F.cmp || ms.length < 2)))
  ) {
    const l = ms.flatMap(lettersOf)[0]!;
    const owner = ks.claims.find((c) => c.at && l in c.at);
    return {
      kind: 'unjustified',
      ...base,
      title: owner ? `Remplace ${l}` : 'Lettre inconnue',
      message: owner
        ? `Remplace d’abord ${l} par la valeur proposée (${owner.atText}), puis calcule.`
        : `« ${l} » n’est pas une grandeur de cette question : on calcule avec des nombres.`,
    };
  }

  if (F.cmp) {
    const [a, b] = ms as [Term, Term];
    const [sa, sb] = shown as [Term, Term];
    const h = constCmpHolds(F.cmp, a, b);
    if (h === null)
      return {
        kind: 'useless',
        ...base,
        unknown: true,
        title: 'Je ne sais pas encore vérifier ce passage',
        message:
          'Je ne sais pas trancher cette comparaison. Garde-la sans HORA, ou calcule d’abord.',
        cmove: { text: A.join(' '), keys: [], terms: [], replaced: [] },
      };
    if (!h)
      return {
        kind: 'false',
        ...base,
        title: 'Ce n’est pas vrai',
        message: `${quoted(sa)} ${F.cmp} ${quoted(sb)} est faux : compare les valeurs.`,
      };
    return {
      kind: 'useless',
      ...base,
      title: 'Comparaison juste',
      message:
        'C’est juste. Dès qu’une valeur est calculée, la feuille compare toute seule : calcule les membres de l’affirmation.',
      cmove: { text: A.join(' '), keys: [], terms: [], replaced: [] },
    };
  }

  // la ligne commence par une affirmation écrite avec ses lettres : on remplace
  let claimRaw: number | null = null;
  const work = ms.slice();
  if (!ks.letters && lettered[0]) {
    const idx = claimOfLetteredLine(ks, ms);
    if (idx === undefined) {
      const l = lettersOf(ms[0]!)[0]!;
      const owner = ks.claims.some((c) => c.at && l in c.at);
      return {
        kind: 'unjustified',
        ...base,
        title: owner ? 'D’où vient-elle ?' : 'Lettre inconnue',
        message: owner
          ? 'Commence par un membre d’une affirmation, puis remplace la lettre par la valeur proposée.'
          : `« ${l} » n’est pas une grandeur de cette question : on calcule avec des nombres.`,
      };
    }
    claimRaw = idx;
    work[0] = substitute(ms[0]!, ks.claims[idx]!.at!);
  }

  // 1. chaque maillon est-il vrai ?
  for (let i = 0; i + 1 < work.length; i++) {
    // « (2 + 1) ÷ (2 − 2) = 3 ÷ 0 » : la même division par 0, calculée (valeur interdite)
    if (key(work[i]!) === key(work[i + 1]!) || sameUndefined(work[i]!, work[i + 1]!)) continue;
    const zl = divisesByZero(work[i]!);
    if (zl !== divisesByZero(work[i + 1]!))
      return {
        kind: 'false',
        ...base,
        title: 'Division par 0',
        message: `On ne divise jamais par 0 : ${quoted(zl ? shown[i]! : shown[i + 1]!)} n’existe pas, elle n’a pas de valeur.`,
      };
    const ul = lnUndefined(work[i]!);
    if (ul || lnUndefined(work[i + 1]!))
      return {
        kind: 'false',
        ...base,
        title: 'Logarithme d’un nombre négatif',
        message: `ln(a) n’existe que pour a > 0 : ${quoted(ul ? shown[i]! : shown[i + 1]!)} n’a pas de valeur.`,
      };
    const s =
      ks.letters && (!isConst(work[i]!) || !isConst(work[i + 1]!))
        ? letterSame(work[i]!, work[i + 1]!)
        : constSameValue(work[i]!, work[i + 1]!);
    if (s === true) continue;
    if (s === null)
      return {
        kind: 'useless',
        ...base,
        unknown: true,
        title: 'Je ne sais pas encore vérifier ce passage',
        message:
          'Ta ligne est peut-être juste, mais je ne sais pas la vérifier. Garde-la sans HORA, ou écris le passage autrement.',
      };
    return falseVerdict(ks, base, ms, i, shown);
  }

  const keys = work.map(key);
  const text = A.join(' ');
  // 2. déjà écrit
  if (
    keys.every((k) => ks.parent.has(k)) &&
    new Set(keys.map((k) => find(ks.parent, k))).size === 1
  )
    return {
      kind: 'dup',
      ...base,
      title: 'Déjà écrit',
      message: 'Ces calculs sont déjà sur ta feuille.',
    };

  // « √15 = √15 » : rien n'est calculé (une telle ligne ne tranche rien). On compare
  // l'ÉCRITURE des membres : « −(−6) = 6 » est une règle des signes (même terme une fois
  // lu, le noyau calcule les nombres à la lecture), pas une ligne vide.
  const raws = rawMembers(A);
  if (raws.length === work.length && new Set(raws).size === 1)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Ta ligne écrit deux fois la même chose. Calcule un membre de l’affirmation.',
    };

  // 3. d'où part la ligne ?
  const steps: (typeof STEP)[StepKind][] = [];
  const replaced: number[] = [];
  let startIdx = 0;
  if (claimRaw !== null) {
    // « 2x + 1 = 2 × 3 + 1 » : le 1er maillon remplace
    if (key(work[0]!) !== key(work[1] ?? work[0]!) && constStep(work[0]!, work[1]!) !== 'same') {
      return {
        kind: 'unjustified',
        ...base,
        title: 'Une étape à la fois',
        message: `Remplace d’abord la lettre par sa valeur (${ks.claims[claimRaw]!.atText}), sans calculer en même temps.`,
      };
    }
    if (!ks.replaced.has(claimRaw)) {
      steps.push(STEP.replace);
      replaced.push(claimRaw);
    }
    startIdx = 1;
  } else {
    // la ligne est remplacée d'emblée : « 2 × 3 + 1 = 7 » pour 2x + 1 (x = 3)
    const sub = ks.claims.find(
      (c) =>
        c.at &&
        !ks.replaced.has(c.index) &&
        c.rels.some(
          (r) =>
            (!isConst(r.rawL) && key(r.l) === keys[0]) ||
            (!isConst(r.rawR) && key(r.r) === keys[0]),
        ),
    );
    if (sub) {
      steps.push(STEP.replace);
      replaced.push(sub.index);
    } else if (!ks.known.has(keys[0]!)) {
      if (keys.slice(1).some((k) => ks.known.has(k)))
        return {
          kind: 'unjustified',
          ...base,
          title: 'Commence par ce que tu calcules',
          message:
            'Écris d’abord l’expression de l’affirmation, puis son calcul : « 11 × 48 = … ».',
        };
      return aside(ks, base, text, keys, work, replaced);
    }
  }

  // 4. chaque maillon en un pas
  const atomsOf = memberAtoms(A);
  let jumped = false;
  for (let i = startIdx; i + 1 < work.length && !jumped; i++) {
    const a = work[i]!;
    const b = work[i + 1]!;
    const aa = atomsOf[i] ?? [];
    const ba = atomsOf[i + 1] ?? [];
    if (sameUndefined(a, b)) {
      if (key(a) !== key(b)) steps.push(STEP.compute);
      continue;
    }
    // moteur v50 : un maillon en lettres, jugé comme sur une feuille « développer »
    if (ks.letters && (!isConst(a) || !isConst(b))) {
      if (key(a) === key(b)) continue;
      const g = letterGesture(a, b);
      if (g) steps.push(STEP[LETTER_STEP[g]]);
      else jumped = true;
      continue;
    }
    const k = specialStep(ks, a, b, aa, ba);
    if (k) {
      steps.push(STEP[k]);
      continue;
    }
    const s = constStep(a, b);
    if (s === 'compute') steps.push(STEP.compute);
    else if (s === 'expand') steps.push(STEP.expand);
    // v45 : « ln(100) = 2ln(10) » n'est pas une même écriture : deux règles de ln d'un coup
    else if ((s === 'reorder' || s === 'same') && (hasLn(a) || hasLn(b)) && lnArgsDiffer(a, b))
      jumped = true;
    else if (s === 'jump') {
      // moteur v41 : une puissance écrite en produit, une règle des puissances à un endroit
      const g = powerGesture41(a, b);
      if (g) steps.push(STEP[g]);
      else jumped = true;
    }
  }

  // 5. ce que la ligne tranche
  const before = kclaimsDecisions(ks);
  const sim = cloneClasses(ks);
  addChain(sim, keys, work);
  const raw = ks.claims.map((c) => decideIn(sim, c));
  const after = eliminate(ks, raw);
  const decided = after.flatMap((d, i) =>
    d !== null && before[i] === null ? [{ index: i, truth: d }] : [],
  );
  const final = after.every((d) => d !== null);
  const cmove: CMove = { text, keys, terms: work, replaced };
  const words = decided.length ? ' ' + decisionWords(ks, decided, raw) : '';

  if (jumped)
    return {
      kind: 'useless',
      ...base,
      jump: true,
      final,
      decided,
      title: decided.length ? 'Juste, tu tranches !' : 'Juste, mais tu sautes des étapes',
      message:
        'Ta ligne est juste. Détaille-la, un calcul par ligne, pour gagner des HORA. Tu peux la garder sans HORA.' +
        words,
      cmove,
    };
  if (!steps.length) {
    if (decided.length)
      return {
        kind: 'step',
        ...base,
        hora: DECIDE.hora,
        final,
        decided,
        rule: { ...DECIDE, why: decisionWords(ks, decided, raw) },
        cmove,
      };
    return {
      kind: 'useless',
      ...base,
      title: 'Même chose, écrite autrement',
      message:
        'Ta ligne est juste, mais elle redit ce que tu as déjà écrit, dans un autre ordre. Avance d’un calcul.',
      cmove,
    };
  }
  const rule = mergeRules(steps.map((s) => ({ ...s, card: CARD })));
  const hora = rule.hora + (decided.length ? DECIDE.hora : 0);
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    decided,
    rule: {
      ...rule,
      hora,
      ...(decided.length ? { label: `${rule.label} → Trancher` } : {}),
      why: rule.why + words,
    },
    cmove,
  };
}

/** le pas d'un maillon en lettres (moteur v50) */
const LETTER_STEP: Record<NonNullable<ReturnType<typeof letterGesture>>, StepKind> = {
  expand: 'expandLetters',
  parens: 'parens',
  compute: 'reduce',
  neutral: 'compute',
  power: 'powerRules',
  powerExpand: 'powerExpand',
  fraction: 'fracSimplify',
  factor: 'factorLetters',
};

function mergeRules(steps: KClaimsRule[]): KClaimsRule {
  if (steps.length === 1) return steps[0]!;
  const labels = steps.map((s) => s.label).filter((l, i, a) => a.indexOf(l) === i);
  const whys = steps.map((s) => s.why).filter((l, i, a) => a.indexOf(l) === i);
  return {
    id: steps[0]!.id,
    card: steps[0]!.card,
    label: labels.join(' → '),
    hora: steps.reduce((s, x) => s + x.hora, 0),
    why: whys.join(' '),
  };
}

function specialStep(
  ks: KClaimsState,
  a: Term,
  b: Term,
  aa: string[],
  ba: string[],
): StepKind | null {
  if (termKey(a) === termKey(b) && signParens(aa) > signParens(ba)) return 'signs';
  // v45 : une règle de ln sur un terme de l'écriture
  const ln = claimsLnGesture(a, b);
  if (ln) return ln;
  // v37 : angles associés, valeur lue sur le cercle, racines
  const g = claimsGesture(a, b, isClaimValue(b));
  // « 3 = √9 » sert à comparer avec une racine de l'affirmation : seulement pour un
  // nombre de l'affirmation (sinon 9 = √81, 81 = √6561… rapporteraient sans fin)
  if (g === 'rootWrite' && !claimMember(ks, a)) return powerStep(a, b) ?? fractionStep(a, b);
  if (g) return g === 'trigValue' ? 'compute' : g;
  // v39 : décomposer en facteurs, regrouper en puissance, puissance de 10
  const f =
    powerStep(a, b) ??
    fractionStep(a, b) ??
    claimsFormGesture(a, b) ??
    // v53 : le numérateur et le dénominateur décomposés en facteurs premiers
    (primeFractionStep(a, b) ? 'fracPrimes' : null);
  return f === 'powerMerge' ? 'powerProduct' : f;
}

/** les logarithmes des deux écritures n'ont pas les mêmes arguments */
function lnArgsDiffer(a: Term, b: Term): boolean {
  const args = (t: Term, out: string[]): string[] => {
    switch (t.k) {
      case 'fn':
        if (t.f === 'ln' || t.f === 'log') out.push(`${t.f}:${key(t.a)}`);
        else args(t.a, out);
        break;
      case 'add':
      case 'mul':
        t.c.forEach((c) => args(c, out));
        break;
      case 'div':
      case 'pow':
      case 'binom':
        args(t.a, out);
        args(t.b, out);
        break;
      default:
        break;
    }
    return out;
  };
  const x = args(a, []).sort().join('|');
  const y = args(b, []).sort().join('|');
  return x !== y;
}

/** t est un membre d'une affirmation (tel qu'écrit) */
function claimMember(ks: KClaimsState, t: Term): boolean {
  const k = key(t);
  return ks.claims.some((c) => c.rels.some((r) => key(r.l) === k || key(r.r) === k));
}

function aside(
  ks: KClaimsState,
  base: Base,
  text: string,
  keys: string[],
  work: Term[],
  replaced: number[],
): KClaimsVerdict {
  const sim = cloneClasses(ks);
  addChain(sim, keys, work);
  const before = kclaimsDecisions(ks);
  const raw = ks.claims.map((c) => decideIn(sim, c));
  const after = eliminate(ks, raw);
  const decided = after.flatMap((d, i) =>
    d !== null && before[i] === null ? [{ index: i, truth: d }] : [],
  );
  return {
    kind: 'useless',
    ...base,
    final: after.every((d) => d !== null),
    decided,
    title: decided.length ? 'Juste, tu tranches !' : 'Juste, mais à côté',
    message: decided.length
      ? decisionWords(ks, decided, raw)
      : 'Ce calcul est juste, mais il ne part d’aucune affirmation : commence par l’une d’elles.',
    cmove: { text, keys, terms: work, replaced },
  };
}

function falseVerdict(
  ks: KClaimsState,
  base: Base,
  ms: Term[],
  i: number,
  shown: Term[] = ms,
): KClaimsVerdict {
  const a = ms[i]!;
  const b = ms[i + 1]!;
  const tries = [`${key(ms[0]!)}=${key(ms[ms.length - 1]!)}`, `${key(a)}=${key(b)}`];
  const t = ks.traps.find((x) => x.keys.some((k) => tries.includes(k)));
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: 'Piège : ' + t.title.charAt(0).toLowerCase() + t.title.slice(1),
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
      badChip: -1,
    };
  // moteur v50 : deux écritures en lettres différentes — un contre-exemple
  if (ks.letters && (!isConst(a) || !isConst(b))) {
    const ce = letterCounterexample(a, b);
    return {
      kind: 'false',
      ...base,
      title: 'Ce n’est pas égal',
      message: ce
        ? `${quoted(shown[i] ?? a)} et ${quoted(shown[i + 1] ?? b)} ne sont pas égales : pour ${ce.at}, la première vaut ${ce.va} et la seconde ${ce.vb}. Refais ce calcul.`
        : `${quoted(shown[i] ?? a)} n’est pas égale à ${formulaText(shown[i + 1] ?? b)} : refais ce calcul.`,
    };
  }
  return {
    kind: 'false',
    ...base,
    title: 'Calcul faux',
    message: `${quoted(shown[i] ?? a)} ne fait pas ${formulaText(shown[i + 1] ?? b)} : refais ce calcul.`,
  };
}

/* ------------------------------ mémoire ------------------------------ */

export function kclaimsCommit(ks: KClaimsState, v: KClaimsVerdict): void {
  const m = v.cmove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  if (m.keys.length) {
    addChain(classesOf(ks), m.keys, m.terms);
    m.keys.forEach((k) => ks.known.add(k));
  }
  m.replaced.forEach((i) => ks.replaced.add(i));
}

function readMembers(ks: KClaimsState, text: string): Term[] | null {
  let atoms: string[];
  try {
    atoms = kclaimsLineAtoms(text);
  } catch {
    return null;
  }
  const F = kforReadAtoms(atoms);
  if (!F || F.cmp || F.approx || F.members.length < 2) return null;
  return ks.eConst ? F.members.map(withE) : F.members;
}

/** la ligne est vraie (chaîne d'égalités exactes, ou comparaison vraie) */
export function kclaimsHolds(ks: KClaimsState, text: string): boolean {
  let atoms: string[];
  try {
    atoms = kclaimsLineAtoms(text);
  } catch {
    return false;
  }
  const F = kforReadAtoms(atoms);
  if (!F || F.approx) return false;
  let ms = ks.eConst ? F.members.map(withE) : F.members;
  // moteur v50 : identités en lettres
  if (ks.letters) {
    if (F.cmp) return constCmpHolds(F.cmp, ms[0]!, ms[1]!) === true;
    for (let i = 0; i + 1 < ms.length; i++) {
      const a = ms[i]!;
      const b = ms[i + 1]!;
      const h = isConst(a) && isConst(b) ? constSameValue(a, b) : letterSame(a, b);
      if (h !== true) return false;
    }
    return true;
  }
  if (ms.some((m) => !isConst(m))) {
    const idx = claimOfLetteredLine(ks, ms);
    if (idx === undefined) return false;
    ms = [substitute(ms[0]!, ks.claims[idx]!.at!), ...ms.slice(1)];
    if (ms.some((m) => !isConst(m))) return false;
  }
  if (F.cmp) return constCmpHolds(F.cmp, ms[0]!, ms[1]!) === true;
  for (let i = 0; i + 1 < ms.length; i++)
    if (constSameValue(ms[i]!, ms[i + 1]!) !== true) return false;
  return true;
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function kclaimsEstablished(ks: KClaimsState, text: string): boolean {
  let ms = readMembers(ks, text);
  if (!ms) return false;
  if (!ks.letters && !isConst(ms[0]!)) {
    const idx = claimOfLetteredLine(ks, ms);
    if (idx === undefined) return false;
    ms = [substitute(ms[0]!, ks.claims[idx]!.at!), ...ms.slice(1)];
  }
  const keys = ms.map(key);
  return (
    keys.every((k) => ks.parent.has(k)) && new Set(keys.map((k) => find(ks.parent, k))).size === 1
  );
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la première ligne du corrigé pas encore écrite */
export function kclaimsNextMove(
  ks: KClaimsState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (kclaimsGoalReached(ks)) return null;
  for (const t of ks.path) {
    if (kclaimsEstablished(ks, t)) continue;
    const atoms = kclaimsLineAtoms(t);
    const v = kclaimsJudge(ks, chipsOf(atoms), { verify: true });
    let line: Line;
    try {
      line = parseText(t);
    } catch {
      continue;
    }
    return { line, text: t, atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kclaimsSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KClaimsVerdict }[];
  reached: boolean;
} {
  const ks = createKClaimsState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KClaimsVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kclaimsLineAtoms(t);
    const v = kclaimsJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kclaimsCommit(ks, v);
    lines.push({ line: v.line!, text: t, atoms, v });
  }
  return { lines, reached: kclaimsGoalReached(ks) };
}

/* ------------------------------ palette ------------------------------ */

/**
 * Les bouts de la feuille (D6 : l'énoncé et le corrigé, jamais les pièges) : les membres
 * des affirmations (un bout chacun, quand ils sont à calculer), les lettres et leur valeur,
 * les nombres de l'énoncé et du corrigé (triés), les opérations utiles.
 */
export function kclaimsPaletteParts(model: ScratchModel): {
  sides: string[][];
  numbers: string[];
  atoms: string[];
  /** moteur v50 : les lettres des identités (« x », « a ») — on calcule avec elles */
  letters: string[];
} {
  const spec = model.kernel!;
  const sides: string[][] = [];
  const seen = new Set<string>();
  // un bout garde l'écriture de l'affirmation (« 3π », « 2√10 ») : la multiplication
  // implicite est ajoutée au jugement, jamais affichée
  const side = (t: Term, atoms: string[]) => {
    if (isClaimValue(t) || !atoms.length) return;
    const k = atoms.join(' ');
    if (!atoms.length || seen.has(k)) return;
    seen.add(k);
    sides.push(atoms);
  };
  const all: string[][] = [];
  for (const c of spec.claims ?? []) {
    for (const text of c.rels) {
      const r = relOf(text);
      let atoms: string[] = [];
      try {
        atoms = kclaimsLineAtoms(text);
      } catch {
        /* illisible */
      }
      all.push(atoms);
      if (!r) {
        // affirmation de propriété (v40) : l'écriture seule est un bout
        const e = exprOf(text);
        if (e && atoms.length) side(e, atoms);
        continue;
      }
      let raw: string[];
      try {
        raw = lex(text);
      } catch {
        continue;
      }
      const parts = memberAtomsAny(raw);
      if (parts.length === 2) {
        side(r.l, parts[0]!);
        side(r.r, parts[1]!);
      }
    }
    if (c.divisibleBy) all.push([c.divisibleBy]);
    for (const v of Object.values(c.at ?? {})) {
      try {
        all.push(kclaimsLineAtoms(v));
      } catch {
        /* illisible */
      }
    }
  }
  for (const t of spec.path ?? []) {
    try {
      all.push(kclaimsLineAtoms(t));
    } catch {
      /* illisible */
    }
  }
  const nums = [...new Set(all.flat().filter((a) => /^\d+(,\d+)?$/.test(a)))];
  nums.sort((a, b) => Number(a.replace(',', '.')) - Number(b.replace(',', '.')));
  // moteur v50 : les lettres d'une identité (sans valeur proposée)
  const eConst = !(spec.claims ?? []).some((c) => c.at && 'e' in c.at);
  const letters = new Set<string>();
  for (const c of spec.claims ?? []) {
    if (c.at && Object.keys(c.at).length) continue;
    for (const text of c.rels) {
      const r = relOf(text, eConst);
      if (!r) continue;
      [...lettersOf(r.l), ...lettersOf(r.r)].forEach((n) => letters.add(n));
    }
  }
  return {
    sides,
    numbers: nums,
    atoms: [...new Set(all.flat())],
    letters: [...letters].sort(),
  };
}

/** membres d'une relation (« = », « < », …) */
function memberAtomsAny(A: string[]): string[][] {
  const out: string[][] = [[]];
  let depth = 0;
  for (const a of A) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && ['=', '<', '>', '≤', '≥'].includes(a)) out.push([]);
    else out[out.length - 1]!.push(a);
  }
  return out;
}

/** texte affiché d'une affirmation (en tête de feuille) */
export function kclaimsTexts(model: ScratchModel): { rels: string[]; at: string | null }[] {
  return (model.kernel?.claims ?? []).map((c) => ({
    rels: c.rels,
    at: c.at
      ? Object.entries(c.at)
          .map(([n, v]) => `${n} = ${v}`)
          .join(' ; ')
      : null,
  }));
}
